import React, { useState, useEffect } from 'react';
import { Plus, Gauge, Droplet, Pencil, AlertTriangle, CheckCircle2, Car } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import toast from 'react-hot-toast';
import RegistrarKmModal from './RegistrarKmModal';
import CambioAceiteModal from './CambioAceiteModal';
import VehiculoFormModal from './VehiculoFormModal';

const ESTADO_CFG = {
  OK: { label: 'Al día', badge: 'bg-emerald-50 text-emerald-600 border-emerald-100', bar: '#059669', icon: CheckCircle2 },
  PROXIMO: { label: 'Próximo cambio', badge: 'bg-amber-50 text-amber-600 border-amber-100', bar: '#d97706', icon: AlertTriangle },
  VENCIDO: { label: 'Cambio vencido', badge: 'bg-red-50 text-red-600 border-red-100', bar: '#dc2626', icon: AlertTriangle },
};

const VehiculoCard = ({ vehiculo, onRegistrarKm, onCambioAceite, onEditar }) => {
  const cfg = ESTADO_CFG[vehiculo.estado_mtto] || ESTADO_CFG.OK;
  const Icon = cfg.icon;
  const progreso = Math.max(0, Math.min(100, 100 - (vehiculo.km_restante_mtto / vehiculo.km_intervalo_mtto) * 100));

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-sura-blue/10 text-sura-blue">
              <Car size={18} />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 text-sm truncate">{vehiculo.nombre}</h3>
              <p className="text-xs text-gray-400 font-mono">{Number(vehiculo.kilometraje_actual).toLocaleString('es-VE')} km</p>
            </div>
          </div>
          <button
            onClick={() => onEditar(vehiculo)}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            title="Editar vehículo"
          >
            <Pencil size={13} />
          </button>
        </div>

        <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg mb-3 w-fit border ${cfg.badge}`}>
          <Icon size={13} />
          <span className="text-xs font-semibold">{cfg.label}</span>
        </div>

        <div className="mb-1">
          <div className="flex items-center justify-between text-[11px] text-gray-400 mb-1">
            <span>Próximo cambio de aceite</span>
            <span>
              {vehiculo.km_restante_mtto > 0
                ? `faltan ${Math.round(vehiculo.km_restante_mtto).toLocaleString('es-VE')} km`
                : `vencido por ${Math.round(Math.abs(vehiculo.km_restante_mtto)).toLocaleString('es-VE')} km`}
            </span>
          </div>
          <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${progreso}%`, background: cfg.bar }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 border-t border-gray-50">
        <button
          onClick={() => onRegistrarKm(vehiculo)}
          className="flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors border-r border-gray-50"
        >
          <Gauge size={13} /> Registrar KM
        </button>
        <button
          onClick={() => onCambioAceite(vehiculo)}
          className="flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-amber-600 hover:bg-amber-50 transition-colors"
        >
          <Droplet size={13} /> Cambio de Aceite
        </button>
      </div>
    </div>
  );
};

const VehiculosTab = () => {
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kmModalVehiculo, setKmModalVehiculo] = useState(null);
  const [aceiteModalVehiculo, setAceiteModalVehiculo] = useState(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editVehiculo, setEditVehiculo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/equipos-moviles/vehiculos');
      setVehiculos(res.data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar los vehículos');
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (vehiculo) => {
    setEditVehiculo(vehiculo);
    setFormModalOpen(true);
  };

  const handleNuevoVehiculo = () => {
    setEditVehiculo(null);
    setFormModalOpen(true);
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={handleNuevoVehiculo}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all shadow-sm bg-sura-blue hover:bg-sura-blue-dark"
        >
          <Plus size={14} /> Nuevo Vehículo
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sura-blue"></div>
        </div>
      ) : vehiculos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-gray-100">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 bg-sura-blue/10">
            <Car size={26} className="text-sura-blue" />
          </div>
          <h3 className="text-gray-800 font-semibold mb-1">Aún no hay vehículos registrados</h3>
          <p className="text-gray-400 text-sm mb-4">Agrega el primer vehículo para comenzar a llevar su kilometraje</p>
          <button
            onClick={handleNuevoVehiculo}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all bg-sura-blue hover:bg-sura-blue-dark"
          >
            <Plus size={14} /> Nuevo Vehículo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehiculos.map(v => (
            <VehiculoCard
              key={v.id}
              vehiculo={v}
              onRegistrarKm={setKmModalVehiculo}
              onCambioAceite={setAceiteModalVehiculo}
              onEditar={handleEditar}
            />
          ))}
        </div>
      )}

      <RegistrarKmModal
        isOpen={!!kmModalVehiculo}
        vehiculo={kmModalVehiculo}
        onClose={() => setKmModalVehiculo(null)}
        onSuccess={fetchData}
      />
      <CambioAceiteModal
        isOpen={!!aceiteModalVehiculo}
        vehiculo={aceiteModalVehiculo}
        onClose={() => setAceiteModalVehiculo(null)}
        onSuccess={fetchData}
      />
      <VehiculoFormModal
        isOpen={formModalOpen}
        vehiculo={editVehiculo}
        onClose={() => setFormModalOpen(false)}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default VehiculosTab;
